/**
 * Entity Linker — Phase 4
 *
 * Allows workspace admins to bulk-map existing priorities.ai entities
 * to their counterparts in connected external systems. This populates
 * the entity_external_ids table so the inbound /apply function can
 * route future changes to the correct local row.
 *
 * Without entries in entity_external_ids, the inbound applier reports
 * "no_mapping" for every event — this page is how you fix that.
 *
 * Route: /prioritization/settings/entity-linker
 */

import { useCallback, useEffect, useState } from 'react';
import {
  AlertTriangle,
  ExternalLink,
  Link2,
  Link2Off,
  Loader2,
  PlusCircle,
  RefreshCcw,
  Search,
  Trash2,
  X,
} from 'lucide-react';
import { PageHeader } from '../../../components/prioritization/PageHeader';
import { getSafeSupabaseClient, getCurrentWorkspaceId } from '../../../lib/supabaseSync';
import { registerExternalId, type ChangeEventEntityType } from '../../../lib/connectors';

// ── Types ─────────────────────────────────────────────────────

interface ExternalIdRow {
  id: string;
  entityType: string;
  internalId: string;
  connectorType: string;
  externalId: string;
  externalUrl: string | null;
  lastSyncedAt: string | null;
  isActive: boolean;
}

interface LocalEntity {
  id: string;
  title: string;
  entityType: string;
}

interface ConnectorConfig {
  id: string;
  connectorType: string;
  displayName: string;
}

// ── Entity type config ────────────────────────────────────────

const ENTITY_TYPES: Array<{ value: ChangeEventEntityType; label: string; table: string; titleColumn: string }> = [
  { value: 'item', label: 'Items', table: 'items', titleColumn: 'title' },
  { value: 'strategy', label: 'Strategies', table: 'strategies', titleColumn: 'name' },
  { value: 'objective', label: 'Objectives', table: 'strategic_objectives', titleColumn: 'name' },
  { value: 'cycle', label: 'Cycles', table: 'degap_cycles', titleColumn: 'name' },
  { value: 'outcome_driver', label: 'Outcome Drivers', table: 'outcome_drivers', titleColumn: 'name' },
  { value: 'desired_outcome', label: 'Desired Outcomes', table: 'desired_outcome_briefs', titleColumn: 'title' },
];

// ── AddLinkModal ──────────────────────────────────────────────

interface AddLinkModalProps {
  entity: LocalEntity;
  connectors: ConnectorConfig[];
  workspaceId: string;
  onAdded: () => void;
  onClose: () => void;
}

function AddLinkModal({ entity, connectors, workspaceId, onAdded, onClose }: AddLinkModalProps) {
  const [connectorType, setConnectorType] = useState(connectors[0]?.connectorType ?? '');
  const [externalId, setExternalId] = useState('');
  const [externalUrl, setExternalUrl] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSave() {
    if (!externalId.trim()) { setError('External ID is required'); return; }
    if (!connectorType) { setError('Select a connector'); return; }
    setIsSaving(true);
    setError(null);
    await registerExternalId({
      workspaceId,
      entityType: entity.entityType as ChangeEventEntityType,
      internalId: entity.id,
      connectorType,
      externalId: externalId.trim(),
      externalUrl: externalUrl.trim() || undefined,
    });
    onAdded();
    onClose();
    setIsSaving(false);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-[var(--bg-raised)] dark:bg-[var(--surface)] shadow-2xl w-full max-w-md border border-[var(--rule)]">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--rule)]">
          <h2 className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">Link external ID</h2>
          <button onClick={onClose} className="text-[var(--text-ter)] hover:text-[var(--text-sec)]">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] bg-[var(--surface)] dark:bg-[var(--bg-raised)]/50 px-3 py-2 font-mono truncate">
            {entity.entityType} / {entity.title}
          </div>

          <div>
            <label className="block text-xs font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-1">Connector</label>
            <select
              value={connectorType}
              onChange={(e) => setConnectorType(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] text-[var(--text-pri)] dark:text-[var(--text-pri)] focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              {connectors.map((c) => (
                <option key={c.connectorType} value={c.connectorType}>
                  {c.displayName || c.connectorType}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-1">
              External ID <span className="text-[var(--coral)]">*</span>
            </label>
            <input
              type="text"
              value={externalId}
              onChange={(e) => setExternalId(e.target.value)}
              placeholder="e.g. PROJ-123, lin_abc123, #42"
              className="w-full px-3 py-2 text-sm border border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] text-[var(--text-pri)] dark:text-[var(--text-pri)] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-1">
              The ID used by the external system (Jira issue key, Linear issue ID, GitHub issue number, etc.)
            </p>
          </div>

          <div>
            <label className="block text-xs font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-1">
              External URL (optional)
            </label>
            <input
              type="url"
              value={externalUrl}
              onChange={(e) => setExternalUrl(e.target.value)}
              placeholder="https://..."
              className="w-full px-3 py-2 text-sm border border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] text-[var(--text-pri)] dark:text-[var(--text-pri)] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          {error && (
            <p className="text-xs text-[var(--coral)] dark:text-[var(--coral)] flex items-center gap-1">
              <AlertTriangle className="h-3 w-3" /> {error}
            </p>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-[var(--rule)]">
          <button onClick={onClose} className="px-4 py-2 text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)] hover:text-[var(--text-pri)] transition-colors">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-[var(--slate)] hover:bg-[var(--slate)] disabled:opacity-50 text-white transition-colors"
          >
            {isSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Link2 className="h-3.5 w-3.5" />}
            Link
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────

export default function EntityLinker() {
  const [entityType, setEntityType] = useState<ChangeEventEntityType>('item');
  const [entities, setEntities] = useState<LocalEntity[]>([]);
  const [mappings, setMappings] = useState<ExternalIdRow[]>([]);
  const [connectors, setConnectors] = useState<ConnectorConfig[]>([]);
  const [search, setSearch] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [addLinkFor, setAddLinkFor] = useState<LocalEntity | null>(null);

  const workspaceId = getCurrentWorkspaceId();

  const load = useCallback(async () => {
    if (!workspaceId) return;
    setIsLoading(true);
    setError(null);
    try {
      const supabase = getSafeSupabaseClient();
      if (!supabase) throw new Error('Supabase not available');

      const entityConfig = ENTITY_TYPES.find((e) => e.value === entityType);
      if (!entityConfig) throw new Error('Unknown entity type');

      const [entResult, mapResult, cfgResult] = await Promise.all([
        supabase
          .from(entityConfig.table)
          .select(`id, ${entityConfig.titleColumn}`)
          .eq('workspace_id', workspaceId)
          .order(entityConfig.titleColumn, { ascending: true })
          .limit(200),
        supabase
          .from('entity_external_ids')
          .select('*')
          .eq('workspace_id', workspaceId)
          .eq('entity_type', entityType)
          .eq('is_active', true),
        supabase
          .from('connector_configs')
          .select('id, connector_type, display_name')
          .eq('workspace_id', workspaceId)
          .eq('is_active', true),
      ]);

      if (entResult.error) throw new Error(entResult.error.message);

      setEntities(
        (entResult.data ?? []).map((row) => ({
          id: row.id as string,
          title: (row[entityConfig.titleColumn as keyof typeof row] as string) || '(untitled)',
          entityType,
        }))
      );

      setMappings(
        (mapResult.data ?? []).map((row) => ({
          id: row.id as string,
          entityType: row.entity_type as string,
          internalId: row.internal_id as string,
          connectorType: row.connector_type as string,
          externalId: row.external_id as string,
          externalUrl: row.external_url as string | null,
          lastSyncedAt: row.last_synced_at as string | null,
          isActive: row.is_active as boolean,
        }))
      );

      setConnectors(
        (cfgResult.data ?? []).map((row) => ({
          id: row.id as string,
          connectorType: row.connector_type as string,
          displayName: (row.display_name as string) || (row.connector_type as string),
        }))
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load entities');
    } finally {
      setIsLoading(false);
    }
  }, [workspaceId, entityType]);

  useEffect(() => { load(); }, [load]);

  async function handleUnlink(mappingId: string) {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return;
    await supabase
      .from('entity_external_ids')
      .update({ is_active: false })
      .eq('id', mappingId);
    load();
  }

  const filteredEntities = entities.filter((e) =>
    !search || e.title.toLowerCase().includes(search.toLowerCase())
  );

  const getMappingsFor = (entityId: string) =>
    mappings.filter((m) => m.internalId === entityId);

  const linkedCount = entities.filter((e) => getMappingsFor(e.id).length > 0).length;

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<Link2 className="h-4 w-4 text-[var(--text-ter)]" />}
        iconBg="bg-[var(--slate-dim)]"
        title="Entity Linker"
        subtitle="Map existing priorities.ai entities to their counterparts in connected external systems. Required for the inbound sync pipeline to apply changes to the correct local row."
      />

      {/* Summary */}
      <div className="grid grid-cols-3 gap-4">
        <div className="p-4 border border-[var(--slate)] dark:border-[var(--slate)] bg-[var(--bg-raised)] dark:bg-[var(--surface)]">
          <div className="text-2xl font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">{entities.length}</div>
          <div className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-0.5">Total {entityType}s</div>
        </div>
        <div className="p-4 border border-[var(--green)] dark:border-[var(--green)] bg-[var(--bg-raised)] dark:bg-[var(--surface)]">
          <div className="text-2xl font-semibold text-[var(--green)] dark:text-[var(--text-ter)]">{linkedCount}</div>
          <div className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-0.5">Linked to external system</div>
        </div>
        <div className="p-4 border border-[var(--mustard)] dark:border-[var(--mustard)] bg-[var(--bg-raised)] dark:bg-[var(--surface)]">
          <div className="text-2xl font-semibold text-[var(--mustard)] dark:text-[var(--mustard)]">
            {entities.length - linkedCount}
          </div>
          <div className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-0.5">Unlinked (inbound applies will miss)</div>
        </div>
      </div>

      {/* Controls */}
      <div className="flex items-center gap-3 flex-wrap">
        {/* Entity type selector */}
        <div className="flex items-center gap-1 p-1 bg-[var(--hover-tint)]">
          {ENTITY_TYPES.map((et) => (
            <button
              key={et.value}
              onClick={() => setEntityType(et.value)}
              className={`px-3 py-1.5 text-xs font-medium  transition-colors ${
                entityType === et.value
                  ? 'bg-[var(--bg-raised)] dark:bg-[var(--surface)] text-[var(--text-pri)] dark:text-[var(--text-pri)] shadow-sm'
                  : 'text-[var(--text-ter)] dark:text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-sec)]'
              }`}
            >
              {et.label}
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[var(--text-ter)]" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search entities…"
            className="w-full pl-9 pr-3 py-2 text-sm border border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] text-[var(--text-pri)] dark:text-[var(--text-pri)] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>

        <button
          onClick={load}
          disabled={isLoading}
          className="flex items-center gap-1.5 px-3 py-2 text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)] bg-[var(--hover-tint)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-lift)] disabled:opacity-50 transition-colors"
        >
          {isLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCcw className="h-3.5 w-3.5" />}
          Refresh
        </button>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-4 bg-[var(--coral-dim)] dark:bg-[var(--coral)]/20 border border-[var(--coral)] dark:border-[var(--coral)] text-sm text-[var(--coral)] dark:text-[var(--coral)]">
          <AlertTriangle className="h-4 w-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {isLoading && filteredEntities.length === 0 && (
        <div className="flex items-center justify-center py-16 text-[var(--text-ter)]">
          <Loader2 className="h-6 w-6 animate-spin mr-2" />
          Loading…
        </div>
      )}

      {/* Entity list */}
      {!isLoading && filteredEntities.length === 0 && (
        <div className="text-center py-16 text-[var(--text-ter)] dark:text-[var(--text-ter)]">
          <Link2Off className="h-8 w-8 mx-auto mb-3 opacity-40" />
          <p className="text-sm font-medium">No {entityType}s found</p>
        </div>
      )}

      {filteredEntities.length > 0 && (
        <div className="border border-[var(--rule)] overflow-hidden bg-[var(--bg-raised)] dark:bg-[var(--surface)]">
          <div className="flex items-center gap-3 px-4 py-2 bg-[var(--surface)] dark:bg-[var(--bg-raised)]/50 border-b border-[var(--rule)] text-xs font-semibold text-[var(--text-ter)] dark:text-[var(--text-ter)] uppercase tracking-wide">
            <span className="flex-1">Entity</span>
            <span className="w-48">External links</span>
            <span className="w-24" />
          </div>
          {filteredEntities.map((entity) => {
            const links = getMappingsFor(entity.id);
            return (
              <div key={entity.id} className="flex items-start gap-3 px-4 py-3 border-b border-[var(--rule)] last:border-0 hover:bg-[var(--surface)] dark:hover:bg-[var(--bg-raised)]/30">
                {/* Entity */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-[var(--text-pri)] dark:text-[var(--text-pri)] truncate">{entity.title}</p>
                  <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] font-mono mt-0.5 truncate">{entity.id}</p>
                </div>

                {/* Links */}
                <div className="w-48 space-y-1">
                  {links.length === 0 ? (
                    <span className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] italic">No links</span>
                  ) : (
                    links.map((link) => (
                      <div key={link.id} className="flex items-center gap-1.5 group">
                        <span className="text-xs font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)]">
                          {link.connectorType}
                        </span>
                        <span className="text-xs font-mono text-[var(--text-ter)] dark:text-[var(--text-ter)] truncate">
                          {link.externalId}
                        </span>
                        {link.externalUrl && (
                          <a href={link.externalUrl} target="_blank" rel="noreferrer" className="text-[var(--text-ter)] hover:text-[var(--text-ter)]">
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        )}
                        <button
                          onClick={() => handleUnlink(link.id)}
                          className="ml-auto text-[var(--text-ter)] hover:text-[var(--coral)] opacity-0 group-hover:opacity-100 transition-opacity"
                          title="Remove link"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </div>
                    ))
                  )}
                </div>

                {/* Add link */}
                <div className="w-24 flex justify-end">
                  <button
                    onClick={() => setAddLinkFor(entity)}
                    disabled={connectors.length === 0}
                    className="flex items-center gap-1 px-2 py-1 text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] hover:bg-[var(--slate-dim)] dark:hover:bg-[var(--slate)]/20 border border-[var(--slate)] dark:border-[var(--slate)] transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                    title={connectors.length === 0 ? 'No active connectors configured' : 'Add external link'}
                  >
                    <PlusCircle className="h-3 w-3" />
                    Link
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add link modal */}
      {addLinkFor && workspaceId && (
        <AddLinkModal
          entity={addLinkFor}
          connectors={connectors}
          workspaceId={workspaceId}
          onAdded={load}
          onClose={() => setAddLinkFor(null)}
        />
      )}

      {/* Info footer */}
      <div className="flex items-start gap-3 px-4 py-3 bg-[var(--surface)] dark:bg-[var(--bg-raised)]/50 border border-[var(--rule)] text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">
        <Link2 className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
        <div>
          <span className="font-medium">Linking is required for inbound sync.</span>{' '}
          When an external system sends a change event, the /apply function looks up the external ID here
          to find the local entity to update. Entities without links will appear as "no_mapping" in the
          Sync Event Log. Links are created automatically when items are imported via a connector.
        </div>
      </div>
    </div>
  );
}
